import React from "react";
import { mutate } from "swr";
import { toast } from "react-toastify";
import {
  IoIosPricetags,
  IoIosSearch,
  IoIosCreate,
  IoIosTrash,
  IoIosGitMerge,
  IoIosArrowForward,
  IoIosCheckmark,
  IoIosClose,
  IoIosCheckmarkCircleOutline,
  IoIosRadioButtonOff,
} from "react-icons/io";
import Nav from "../../component/Nav";
import Modal from "../../component/Modal";
import { useTags, renameTag, deleteTag, mergeTags } from "../../api";
import { FormatNumber } from "../../helpers";
import "./tag-management.css";

const SORT_OPTIONS = [
  { value: "usage", label: "Paling sering" },
  { value: "name", label: "Nama (A–Z)" },
  { value: "recent", label: "Terbaru dipakai" },
];

function formatDateShort(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const months = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Ags", "Sep", "Okt", "Nov", "Des"];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

export default function TagManagement(props) {
  const { data, error } = useTags();
  const tags = data || [];
  const [search, setSearch] = React.useState("");
  const [sort, setSort] = React.useState("usage");
  const [bulk, setBulk] = React.useState(false);
  const [selected, setSelected] = React.useState(new Set());
  const [renameTarget, setRenameTarget] = React.useState(null);
  const [renameValue, setRenameValue] = React.useState("");
  const [renameError, setRenameError] = React.useState("");
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [mergeModal, setMergeModal] = React.useState(false);
  const [mergeInto, setMergeInto] = React.useState("");
  const [mergeError, setMergeError] = React.useState("");
  const [bulkDelete, setBulkDelete] = React.useState(false);

  const exitBulk = () => {
    setBulk(false);
    setSelected(new Set());
  };

  const filtered = React.useMemo(() => {
    let list = tags;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((t) => t.tag.toLowerCase().includes(q));
    }
    const sorted = [...list];
    if (sort === "name") {
      sorted.sort((a, b) => a.tag.localeCompare(b.tag));
    } else if (sort === "usage") {
      sorted.sort(
        (a, b) =>
          b.usageCount - a.usageCount || a.tag.localeCompare(b.tag)
      );
    } else if (sort === "recent") {
      sorted.sort((a, b) => {
        const ta = a.lastUsed ? new Date(a.lastUsed).getTime() : 0;
        const tb = b.lastUsed ? new Date(b.lastUsed).getTime() : 0;
        return tb - ta || a.tag.localeCompare(b.tag);
      });
    }
    return sorted;
  }, [tags, search, sort]);

  const toggleSelect = (name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const openRename = (t) => {
    setRenameTarget(t);
    setRenameValue(t.tag);
    setRenameError("");
  };

  const submitRename = async () => {
    const v = renameValue.trim().toLowerCase();
    if (!v) {
      setRenameError("Nama tag tidak boleh kosong");
      return;
    }
    if (v === renameTarget.tag) {
      setRenameTarget(null);
      return;
    }
    try {
      await renameTag(renameTarget.tag, v);
      toast.success(`Tag diubah ke "${v}"`);
      await mutate("/tag");
      setRenameTarget(null);
    } catch (err) {
      const status = err.response && err.response.status;
      if (status === 409) {
        setRenameError(
          `Tag "${v}" sudah ada. Tutup dan pilih "Gabungkan" dari menu untuk menggabungkannya.`
        );
      } else {
        setRenameError("Gagal mengubah nama");
      }
    }
  };

  const submitDelete = async () => {
    try {
      await deleteTag(deleteTarget.tag);
      toast.success(`Tag "${deleteTarget.tag}" dihapus`);
      await mutate("/tag");
    } catch (_) {
      toast.error("Gagal menghapus tag");
    } finally {
      setDeleteTarget(null);
    }
  };

  const openMerge = () => {
    if (selected.size < 2) {
      toast.info("Pilih minimal 2 tag untuk digabungkan");
      return;
    }
    setMergeInto("");
    setMergeError("");
    setMergeModal(true);
  };

  const submitMerge = async () => {
    const into = mergeInto.trim().toLowerCase();
    if (!into) {
      setMergeError("Pilih atau ketik tag tujuan");
      return;
    }
    const from = Array.from(selected).filter((t) => t !== into);
    if (from.length === 0) {
      setMergeError("Tag tujuan harus berbeda dari semua tag asal");
      return;
    }
    try {
      await mergeTags(from, into);
      toast.success(`${from.length} tag digabungkan ke "${into}"`);
      await mutate("/tag");
      setMergeModal(false);
      exitBulk();
    } catch (_) {
      setMergeError("Gagal menggabungkan tag");
    }
  };

  const submitBulkDelete = async () => {
    const names = Array.from(selected);
    try {
      await Promise.all(names.map((n) => deleteTag(n)));
      toast.success(`${names.length} tag dihapus`);
      await mutate("/tag");
      exitBulk();
    } catch (_) {
      toast.error("Sebagian tag gagal dihapus");
      await mutate("/tag");
    } finally {
      setBulkDelete(false);
    }
  };

  const navigateToTag = (t) => {
    localStorage.setItem("tag", JSON.stringify({ tag: t.tag }));
    props.history.push("/transaction-search");
  };

  return (
    <>
      <Nav title="Kelola Tag" icon={IoIosPricetags} />
      <div className="tag-mgmt">
        {/* Top controls */}
        <div className="tag-mgmt__controls">
          <div className="tag-mgmt__search">
            <IoIosSearch className="tag-mgmt__search-icon" />
            <input
              type="search"
              className="tag-mgmt__search-input"
              placeholder="Cari tag…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="tag-mgmt__sort-row">
            <div className="tag-mgmt__sort-chips">
              {SORT_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={`tag-mgmt__sort-chip ${
                    sort === o.value ? "tag-mgmt__sort-chip--active" : ""
                  }`}
                  onClick={() => setSort(o.value)}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`tag-mgmt__bulk-toggle ${
                bulk ? "tag-mgmt__bulk-toggle--active" : ""
              }`}
              onClick={() => (bulk ? exitBulk() : setBulk(true))}
            >
              {bulk ? (
                <>
                  <IoIosClose /> Batal
                </>
              ) : (
                <>
                  <IoIosCheckmarkCircleOutline /> Pilih
                </>
              )}
            </button>
          </div>
        </div>

        {/* States */}
        {!data && !error && (
          <div className="tag-mgmt__placeholder">Memuat…</div>
        )}
        {data && tags.length === 0 && (
          <div className="tag-mgmt__empty">
            <IoIosPricetags className="tag-mgmt__empty-icon" />
            <div className="tag-mgmt__empty-title">Belum ada tag</div>
            <div className="tag-mgmt__empty-desc">
              Tag muncul otomatis setelah Anda menambahkannya pada
              transaksi.
            </div>
            <button
              type="button"
              className="btn btn-warning btn-sm mt-3"
              onClick={() => props.history.push("/transaction/add")}
            >
              Catat transaksi
            </button>
          </div>
        )}
        {data && tags.length > 0 && filtered.length === 0 && (
          <div className="tag-mgmt__placeholder">
            Tidak ada tag yang cocok dengan "{search}".
          </div>
        )}

        {/* List */}
        {filtered.length > 0 && (
          <ul className="tag-mgmt__list">
            {filtered.map((t) => {
              const isSelected = selected.has(t.tag);
              const lastUsed = formatDateShort(t.lastUsed);
              return (
                <li
                  key={t.tag}
                  className={`tag-mgmt__row ${
                    isSelected ? "tag-mgmt__row--selected" : ""
                  }`}
                  onClick={() => (bulk ? toggleSelect(t.tag) : navigateToTag(t))}
                >
                  {bulk && (
                    <span className="tag-mgmt__checkbox">
                      {isSelected ? (
                        <IoIosCheckmarkCircleOutline />
                      ) : (
                        <IoIosRadioButtonOff />
                      )}
                    </span>
                  )}
                  <span className="tag-mgmt__chip">{t.tag}</span>
                  <div className="tag-mgmt__meta">
                    <div className="tag-mgmt__meta-line">
                      {t.usageCount} transaksi
                      {t.totalAmount > 0 && (
                        <>
                          {" · "}Rp {FormatNumber(Math.round(t.totalAmount))}
                        </>
                      )}
                    </div>
                    {lastUsed && (
                      <div className="tag-mgmt__meta-sub">
                        Terakhir: {lastUsed}
                      </div>
                    )}
                  </div>
                  {!bulk && (
                    <div
                      className="tag-mgmt__actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="tag-mgmt__action-btn"
                        title="Rename"
                        onClick={() => openRename(t)}
                      >
                        <IoIosCreate />
                      </button>
                      <button
                        type="button"
                        className="tag-mgmt__action-btn tag-mgmt__action-btn--danger"
                        title="Hapus"
                        onClick={() => setDeleteTarget(t)}
                      >
                        <IoIosTrash />
                      </button>
                      <IoIosArrowForward className="tag-mgmt__row-chevron" />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {/* Bulk action bar */}
        {bulk && selected.size > 0 && (
          <div className="tag-mgmt__bulkbar">
            <div className="tag-mgmt__bulkbar-count">
              {selected.size} dipilih
            </div>
            <div className="tag-mgmt__bulkbar-actions">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={openMerge}
                disabled={selected.size < 2}
              >
                <IoIosGitMerge /> Gabungkan
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => setBulkDelete(true)}
              >
                <IoIosTrash /> Hapus
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Rename modal */}
      <Modal
        show={!!renameTarget}
        title="Ubah Nama Tag"
        onClose={() => setRenameTarget(null)}
        onPositive={submitRename}
        onNegative={() => setRenameTarget(null)}
        textPositive="Simpan"
        textNegative="Batal"
      >
        <div className="mb-2">
          Tag lama: <strong>{renameTarget && renameTarget.tag}</strong>
        </div>
        <input
          type="text"
          className="form-control"
          value={renameValue}
          autoFocus
          onChange={(e) => setRenameValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submitRename()}
          placeholder="Nama baru…"
        />
        {renameError && (
          <small className="text-danger d-block mt-1">{renameError}</small>
        )}
      </Modal>

      {/* Delete modal */}
      <Modal
        show={!!deleteTarget}
        title="Hapus Tag"
        onClose={() => setDeleteTarget(null)}
        onPositive={submitDelete}
        onNegative={() => setDeleteTarget(null)}
        textPositive="Hapus"
        textNegative="Batal"
      >
        Tag <strong>{deleteTarget && deleteTarget.tag}</strong> akan dihapus
        dari{" "}
        <strong>
          {deleteTarget && deleteTarget.usageCount} transaksi
        </strong>
        . Transaksi itu sendiri tetap ada — hanya kehilangan tag ini.
      </Modal>

      {/* Bulk delete modal */}
      <Modal
        show={bulkDelete}
        title="Hapus Beberapa Tag"
        onClose={() => setBulkDelete(false)}
        onPositive={submitBulkDelete}
        onNegative={() => setBulkDelete(false)}
        textPositive="Hapus"
        textNegative="Batal"
      >
        <strong>{selected.size} tag</strong> akan dihapus. Transaksi yang
        memakainya tetap ada — hanya kehilangan tag ini.
      </Modal>

      {/* Merge modal */}
      <Modal
        show={mergeModal}
        title="Gabungkan Tag"
        onClose={() => setMergeModal(false)}
        onPositive={submitMerge}
        onNegative={() => setMergeModal(false)}
        textPositive="Gabungkan"
        textNegative="Batal"
      >
        <div className="mb-2">Tag asal:</div>
        <div className="tag-mgmt__merge-chips">
          {Array.from(selected).map((t) => (
            <span key={t} className="tag-mgmt__chip tag-mgmt__chip--mini">
              {t}
            </span>
          ))}
        </div>
        <div className="mt-3 mb-1">Gabungkan ke:</div>
        <input
          type="text"
          className="form-control"
          list="tag-merge-options"
          value={mergeInto}
          onChange={(e) => setMergeInto(e.target.value)}
          placeholder="Pilih tag tujuan, atau ketik baru…"
        />
        <datalist id="tag-merge-options">
          {Array.from(selected).map((t) => (
            <option key={t} value={t} />
          ))}
        </datalist>
        {mergeError && (
          <small className="text-danger d-block mt-1">{mergeError}</small>
        )}
        <small className="d-block text-muted mt-2">
          <IoIosCheckmark /> Semua transaksi yang memakai tag asal akan
          otomatis berpindah ke tag tujuan.
        </small>
      </Modal>
    </>
  );
}
